import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { Button } from '@/components/ui/button';
import { TiptapRichTextEditor } from 'components/DesignSystems/New/TiptapRichTextEditor';
import AutomatedLayerStatsSectionPanel, {
  isAutomatedLayerStatsSection,
} from 'components/DisasterGPT/AutomatedLayerStatsSectionPanel';
import AutomatedSeeristSectionPanel, {
  isAutomatedSeeristSection,
} from 'components/DisasterGPT/AutomatedSeeristSectionPanel';
import { isOpordTasksSection } from 'components/DisasterGPT/constants/opordSectionDefaults';
import {
  SitrepSectionType,
  useAddImageToSitrep,
  useDeleteSitrepImage,
  useSitrepSectionImages,
} from 'components/DisasterGPT/hooks/useSitrepsV2';
import { CollaborativeSitrepEditor } from 'components/Reports/collab/CollaborativeSitrepEditor';
import { useSectionContentEditor } from 'components/Reports/document/useSectionContentEditor';
import AutomatedEntityListSectionPanel, {
  isAutomatedActionsSection,
  isAutomatedIncidentsSection,
  isAutomatedOrgEventsSection,
} from 'components/Reports/automated/AutomatedEntityListSectionPanel';
import ChildAorReportSectionPanel, {
  isAutomatedChildAorReportSection,
} from 'components/Reports/automated/ChildAorReportSectionPanel';
import { ReportMentionDetailDialog } from 'components/Reports/mentions/ReportMentionDetailDialog';
import { useReportMentionDialog } from 'components/Reports/mentions/useReportMentionDialog';
import { useReportMentionItems } from 'components/Reports/mentions/useReportMentionItems';
import type { GetSitrepsResponse } from 'components/Reports/reports.types';
import { ReportsGeneratingPlaceholder } from 'components/Reports/ReportsGeneratingPlaceholder';
import SitrepSectionConflictModal from 'components/Reports/SitrepSectionConflictModal';
import SitrepTasksActionsPanel from 'components/Reports/SitrepTasksActionsPanel';
import type { ActionEntity } from 'components/IAP/hooks/useScopedActionForm';
import DynamicSectionDataToggle from 'components/Reports/section/DynamicSectionDataToggle';
import {
  parseOpordTasksSnapshot,
  snapshotVersion,
  type DynamicSectionDataMode,
} from 'components/Reports/section/dynamicSectionSnapshot';
import { cn } from '@/lib/utils';
import { selectGroupGuid } from 'slices/commonSelectors';
import { useSitrepCollabFlag } from 'utils/featureFlags';

export type SitrepSectionEditor = ReturnType<typeof useSectionContentEditor>;

type SitrepSectionBodyProps = {
  section: SitrepSectionType;
  sitrepId: string;
  sitrep?: Pick<
    GetSitrepsResponse,
    'aor_id' | 'incident_id' | 'approval_status'
  >;
  sitrepWorkspaceId: string | null;
  sitrepTemplateId?: string | null;
  canEdit: boolean;
  editor: SitrepSectionEditor;
  /** Tabs view shows Save / Cancel under the editor. Document view does not. */
  showInlineSaveActions?: boolean;
  onContentChange?: (html: string) => void;
  hideBodyEditor?: boolean;
  dataStringOverride?: string | null;
  hideRun?: boolean;
  useLiveEnrichment?: boolean;
  onRefreshAnalytics?: () => void;
  refreshingAnalytics?: boolean;
};

export function useSitrepSectionEditor(params: {
  section: SitrepSectionType;
  sitrepId: string;
  sitrepWorkspaceId: string | null;
  canEdit: boolean;
  onUpdated?: () => void;
  silent?: boolean;
  isAiLocked?: boolean;
}): {
  editor: SitrepSectionEditor;
  sitrepImages: ReturnType<typeof useSitrepSectionImages>['data'];
  uploadSitrepImage: ReturnType<typeof useAddImageToSitrep>['mutateAsync'];
  removeImageFromSitrep: ReturnType<typeof useDeleteSitrepImage>['mutateAsync'];
} {
  const groupId = useSelector(selectGroupGuid);
  const imageOwnerId = params.sitrepWorkspaceId || groupId;
  const { mutateAsync: uploadSitrepImage } = useAddImageToSitrep(
    params.sitrepId,
    params.section.id,
    imageOwnerId
  );
  const { data: sitrepImages } = useSitrepSectionImages(params.section.id);
  const { mutateAsync: removeImageFromSitrep } = useDeleteSitrepImage(
    params.sitrepId,
    params.section.id,
    imageOwnerId
  );
  const sitrepCollabEnabled = useSitrepCollabFlag();
  const editor = useSectionContentEditor({
    section: params.section,
    sitrepId: params.sitrepId,
    sitrepImages: sitrepImages || [],
    canEdit: params.canEdit,
    onUpdated: params.onUpdated,
    silent: params.silent,
    enableCollab: sitrepCollabEnabled,
    isAiLocked: params.isAiLocked,
  });

  return { editor, sitrepImages, uploadSitrepImage, removeImageFromSitrep };
}

export function SitrepSectionBody({
  section,
  sitrepId,
  sitrep,
  sitrepWorkspaceId,
  sitrepTemplateId = null,
  canEdit,
  editor,
  showInlineSaveActions = false,
  onContentChange,
  hideBodyEditor = false,
  dataStringOverride,
  hideRun = false,
  useLiveEnrichment = true,
  onRefreshAnalytics,
  refreshingAnalytics = false,
}: SitrepSectionBodyProps) {
  const { items: mentionItems } = useReportMentionItems();
  const {
    mention: activeMention,
    open: mentionDialogOpen,
    onOpenChange: onMentionDialogOpenChange,
    onMentionClick,
  } = useReportMentionDialog();

  const groupId = useSelector(selectGroupGuid);
  const imageOwnerId = sitrepWorkspaceId || groupId;
  const { mutateAsync: uploadSitrepImage } = useAddImageToSitrep(
    sitrepId,
    section.id,
    imageOwnerId
  );
  const { data: sitrepImages } = useSitrepSectionImages(section.id);

  const {
    isEditing,
    content,
    conflict,
    isSaving,
    sectionText,
    isProcessing,
    canInteract,
    ydoc,
    collabEnabled,
    startEditing,
    cancelEditing,
    save,
    onContentChange: editorOnContentChange,
    overrideConflict,
    keepExisting,
  } = editor;

  const isAutomatedSeerist = isAutomatedSeeristSection(section);
  const isAutomatedLayerStats = isAutomatedLayerStatsSection(section);
  const isAutomatedOrgEvents = isAutomatedOrgEventsSection(section);
  const isAutomatedIncidents = isAutomatedIncidentsSection(section);
  const isAutomatedActions = isAutomatedActionsSection(section);
  const isAutomatedChildAorReport = isAutomatedChildAorReportSection(section);
  const isAutomated =
    isAutomatedSeerist ||
    isAutomatedLayerStats ||
    isAutomatedOrgEvents ||
    isAutomatedIncidents ||
    isAutomatedActions ||
    isAutomatedChildAorReport;
  const showTasksActionsPanel = isOpordTasksSection({
    sectionName: section.name,
    outputFormat: section.output_format,
    sitrepTemplateId,
    sectionTemplateId: section.section_template_id,
  });
  const tasksReviewStatus = sitrep?.approval_status;
  const showTasksSnapshotToggle =
    showTasksActionsPanel &&
    (tasksReviewStatus === 'PENDING' || tasksReviewStatus === 'APPROVED');
  const [tasksDataMode, setTasksDataMode] =
    useState<DynamicSectionDataMode>('live');
  const storedTasksSnapshot = parseOpordTasksSnapshot(
    snapshotVersion(section)?.data_string
  );
  const showLiveTasksEditor =
    isAutomatedActions && hideRun && useLiveEnrichment;
  const tasksVersion = section.section_versions?.[0];
  const tasksRefreshKey = tasksVersion
    ? [
        tasksVersion.version_id ?? '',
        tasksVersion.status ?? '',
        String((tasksVersion.sitrep_section_text || '').length),
      ].join(':')
    : null;

  const handleContentChange = (html: string) => {
    editorOnContentChange(html);
    onContentChange?.(html);
  };

  const handleStartEditKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      startEditing();
    }
  };

  const emptyLabel = canInteract
    ? 'Click to add content'
    : 'No content added yet';

  const readOnlyEditor = sectionText ? (
    <TiptapRichTextEditor
      value={sectionText}
      readOnly
      showToolbar={false}
      className="reports-section__html-content"
      imageLibrary={sitrepImages || []}
      normalizeMarkdown
      mentionsEnabled
      mentionItems={mentionItems}
      onMentionClick={onMentionClick}
    />
  ) : (
    <em className="tw:text-muted-foreground">{emptyLabel}</em>
  );

  return (
    <div className="reports-section tw:text-foreground">
      {hideBodyEditor ? null : isAutomatedLayerStats ? (
        <AutomatedLayerStatsSectionPanel
          sitrepId={sitrepId}
          sitrep={sitrep}
          section={section}
          disabled={!canEdit}
          dataStringOverride={dataStringOverride}
          hideRun={hideRun}
          onRefreshAnalytics={onRefreshAnalytics}
          refreshingAnalytics={refreshingAnalytics}
        />
      ) : isAutomatedOrgEvents ? (
        <AutomatedEntityListSectionPanel
          kind="orgEvents"
          sitrepId={sitrepId}
          sitrep={sitrep}
          section={section}
          disabled={!canEdit}
          dataStringOverride={dataStringOverride}
          hideRun={hideRun}
          useLiveEnrichment={useLiveEnrichment}
        />
      ) : isAutomatedIncidents ? (
        <AutomatedEntityListSectionPanel
          kind="incidents"
          sitrepId={sitrepId}
          sitrep={sitrep}
          section={section}
          disabled={!canEdit}
          dataStringOverride={dataStringOverride}
          hideRun={hideRun}
          useLiveEnrichment={useLiveEnrichment}
        />
      ) : isAutomatedActions && showLiveTasksEditor ? (
        <SitrepTasksActionsPanel
          incidentId={sitrepWorkspaceId}
          aorId={sitrep?.aor_id}
          canEdit
        />
      ) : isAutomatedActions ? (
        <AutomatedEntityListSectionPanel
          kind="actions"
          sitrepId={sitrepId}
          sitrep={sitrep}
          section={section}
          disabled={!canEdit}
          dataStringOverride={dataStringOverride}
          hideRun={hideRun}
          useLiveEnrichment={useLiveEnrichment}
        />
      ) : isAutomatedSeerist ? (
        <AutomatedSeeristSectionPanel
          sitrepId={sitrepId}
          section={section}
          disabled={!canEdit}
        />
      ) : isAutomatedChildAorReport ? (
        <ChildAorReportSectionPanel
          sitrepId={sitrepId}
          section={section}
          disabled={!canEdit}
        />
      ) : isProcessing ? (
        <ReportsGeneratingPlaceholder />
      ) : isEditing ? (
        <div className="tw:space-y-2" data-testid="reports-doc-section-editor">
          {collabEnabled && ydoc ? (
            <CollaborativeSitrepEditor
              ydoc={ydoc}
              initialHtml={content || sectionText}
              onHtmlChange={handleContentChange}
              placeholder="Enter section content..."
              disabled={isSaving}
              minHeight={showInlineSaveActions ? '150px' : '120px'}
              className="w-100"
            />
          ) : (
            <TiptapRichTextEditor
              value={content}
              onChange={handleContentChange}
              placeholder="Enter section content... Type @ to mention a resource, action, or layer"
              disabled={isSaving}
              minHeight={showInlineSaveActions ? '150px' : '120px'}
              autoFocus
              className="w-100"
              onClickUploadImage={async (files: File[]) => {
                const uploaded = [];
                for (const file of files) {
                  uploaded.push(await uploadSitrepImage({ files: [file] }));
                }
                return uploaded;
              }}
              imageLibrary={sitrepImages || []}
              normalizeMarkdown
              mentionsEnabled
              mentionItems={mentionItems}
              onMentionClick={onMentionClick}
            />
          )}
          {showInlineSaveActions ? (
            <div className="tw:flex tw:gap-2">
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  void save();
                }}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={cancelEditing}
              >
                Cancel
              </Button>
            </div>
          ) : null}
        </div>
      ) : (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- when interactive this div carries role="button" + tabIndex + keyboard handler
        <div
          className={cn(
            'reports-doc__section-text',
            canInteract && 'reports-doc__section-text--editable'
          )}
          onClick={canInteract ? startEditing : undefined}
          onKeyDown={canInteract ? handleStartEditKeyDown : undefined}
          role={canInteract ? 'button' : undefined}
          tabIndex={canInteract ? 0 : undefined}
          title={canInteract ? 'Click to edit content' : undefined}
        >
          {isAutomated ? null : isProcessing && section.type === 'ai' ? (
            <ReportsGeneratingPlaceholder />
          ) : (
            readOnlyEditor
          )}
        </div>
      )}

      {showTasksActionsPanel ? (
        <div className="tw:mt-3 tw:space-y-3">
          {showTasksSnapshotToggle ? (
            <DynamicSectionDataToggle
              approvalStatus={tasksReviewStatus}
              mode={tasksDataMode}
              onModeChange={setTasksDataMode}
            />
          ) : null}
          <SitrepTasksActionsPanel
            incidentId={sitrepWorkspaceId}
            aorId={sitrep?.aor_id}
            canEdit={
              showTasksSnapshotToggle ? tasksDataMode === 'live' : canEdit
            }
            snapshotActions={
              showTasksSnapshotToggle &&
              tasksDataMode === 'snapshot' &&
              storedTasksSnapshot
                ? (storedTasksSnapshot as ActionEntity[])
                : undefined
            }
            className={showTasksSnapshotToggle ? 'tw:mt-0' : undefined}
            refreshKey={tasksRefreshKey}
          />
        </div>
      ) : null}

      <ReportMentionDetailDialog
        mention={activeMention}
        open={mentionDialogOpen}
        onOpenChange={onMentionDialogOpenChange}
      />
      <SitrepSectionConflictModal
        show={!!conflict}
        conflict={conflict}
        draftContent={content}
        onKeepExisting={keepExisting}
        onOverride={overrideConflict}
        isOverriding={isSaving}
      />
    </div>
  );
}
